/**
 * [CCI-17658] Small accessors for the `slices[] + appliedIndex` slice-history
 * model on `AIContentPositionEntry` (see `pm-plugins/ai-session-context/types.ts`).
 *
 * They centralise the `noUncheckedIndexedAccess` fallback so callers always get a
 * concrete `Slice`, and encode the undo/redo availability rules in one place.
 *
 *   - `slices[0]`               = original (pre-AI) content.
 *   - `slices[appliedIndex]`    = the content currently at `[startPos, endPos]`.
 *   - undo available            = `appliedIndex > 0`.
 *   - redo available            = `appliedIndex < slices.length - 1`.
 */
import { Slice } from '@atlaskit/editor-prosemirror/model';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';

/** The original (pre-AI) slice — `slices[0]`. Falls back to empty if absent. */
export const originalSliceOf = (entry: AIContentPositionEntry): Slice =>
	entry.slices[0] ?? Slice.empty;

/** The currently-applied slice — `slices[appliedIndex]`. Falls back to empty. */
export const appliedSliceOf = (entry: AIContentPositionEntry): Slice =>
	entry.slices[entry.appliedIndex] ?? Slice.empty;

/** Undo is available while the entry is not already at its original slice. */
export const entryCanUndo = (entry: AIContentPositionEntry): boolean => entry.appliedIndex > 0;

/** Redo is available while the entry is not already at its latest slice. */
export const entryCanRedo = (entry: AIContentPositionEntry): boolean =>
	entry.appliedIndex < entry.slices.length - 1;

/**
 * ADF mark types that are ignored when deciding whether an entry changed:
 *
 *   - `breakout` — a layout-width mark (`mode: wide|full-width`) that a
 *     whole-node/whole-doc replace (e.g. `replaceDoc`) can drop or re-add without
 *     the reviewable content actually changing.
 */
const IGNORED_MARK_TYPES = new Set(['breakout']);

/**
 * Deep-normalise a value for change comparison so two structures that differ
 * ONLY by ignorable metadata compare equal. Specifically it:
 *
 *   - strips every `localId` (at any nesting): an ephemeral, per-node identity
 *     stamp that lives both as a node attribute (`attrs.localId`) and as a
 *     `fragment` mark attribute. A whole-node/whole-doc replace (e.g.
 *     `replaceDoc`) re-emits content with FRESH localIds, so the applied slice
 *     legitimately carries different localIds even when the content is identical.
 *   - drops ignorable marks (`IGNORED_MARK_TYPES`, e.g. `breakout`) from every
 *     `marks` array, and removes a `marks` key that becomes empty so `marks: []`
 *     and an absent `marks` compare equal.
 *
 * Removing these regardless of nesting handles all carriers without needing to
 * know the schema.
 */
const normaliseForChangeCompare = (value: unknown): unknown => {
	if (Array.isArray(value)) {
		return value.map(normaliseForChangeCompare);
	}
	if (value && typeof value === 'object') {
		const result: Record<string, unknown> = {};
		for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
			if (key === 'localId') {
				continue;
			}
			if (key === 'marks' && Array.isArray(child)) {
				const kept = child.filter(
					(mark) =>
						!(
							mark &&
							typeof mark === 'object' &&
							IGNORED_MARK_TYPES.has((mark as { type?: unknown }).type as string)
						),
				);
				// Drop an empty `marks` array so `marks: []` == absent `marks`.
				if (kept.length > 0) {
					result[key] = kept.map(normaliseForChangeCompare);
				}
				continue;
			}
			result[key] = normaliseForChangeCompare(child);
		}
		return result;
	}
	return value;
};

/**
 * True when two slices represent the same content IGNORING ephemeral metadata
 * (`localId` and ignorable marks such as `breakout` — see
 * `normaliseForChangeCompare`).
 *
 * We cannot use `Slice.eq` here: it delegates to `Node.eq` → `Node.sameMarkup`,
 * which `compareDeep`s ALL attributes and marks — so two paragraphs with
 * identical text but different `localId`s (or a dropped `breakout`) are reported
 * as different. That would leak unchanged segments into the review whenever the
 * AI re-stamps localIds or re-flows layout (the `replaceDoc` case). We instead
 * compare the normalised JSON, plus the open depths (`openStart` / `openEnd`)
 * that `Slice.eq` also considers.
 */
export const slicesEqualIgnoringLocalId = (a: Slice, b: Slice): boolean => {
	if (a === b) {
		return true;
	}
	if (a.openStart !== b.openStart || a.openEnd !== b.openEnd) {
		return false;
	}
	// `Fragment.toJSON()` returns `null` for empty content; normalise so two
	// empty slices (whatever their provenance) still compare equal.
	const aContent = normaliseForChangeCompare(a.content.toJSON() ?? null);
	const bContent = normaliseForChangeCompare(b.content.toJSON() ?? null);
	return JSON.stringify(aContent) === JSON.stringify(bContent);
};

/**
 * True when the entry carries no reviewable change, i.e. its original
 * slice is identical to its currently-applied slice, ignoring ephemeral
 * `localId`s (see `slicesEqualIgnoringLocalId`).
 *
 * These entries add nothing to review — they would make the user step through a
 * "change" where nothing changed — so they are filtered out of the working
 * segments before they are stored in plugin state.
 */
export const entryHasNoChange = (entry: AIContentPositionEntry): boolean =>
	slicesEqualIgnoringLocalId(originalSliceOf(entry), appliedSliceOf(entry));
